// src/screens/UserJourneyScreen.js
import React, { useState } from 'react';
import { B } from '../constants';
import Input from '../components/ui/Input';

export default function UserJourneyScreen({ goScreen, user, onAuthRequired, setTripJourneyData }) {
  const [location, setLocation] = useState('');
  const [tripType, setTripType] = useState(null); // 'plan' | 'booked'

  const handleContinue = () => {
    if (!user) {
      setTripJourneyData({ destination: location.trim(), type: tripType || 'new' });
      onAuthRequired();
      return;
    }
    if (!location.trim()) return;
    if (tripType === 'plan') {
      goScreen('tripPlanner', { destination: location.trim(), type: 'new' });
    } else if (tripType === 'booked') {
      goScreen('tripPlanner', { destination: location.trim(), type: 'booked' });
    }
  };

  const popularDestinations = [
    { name: 'Paris', emoji: '🗼' },
    { name: 'Bali', emoji: '🏝️' },
    { name: 'Tokyo', emoji: '🗾' },
    { name: 'New York', emoji: '🗽' },
    { name: 'London', emoji: '🎡' },
    { name: 'Dubai', emoji: '🏙️' },
    { name: 'Maldives', emoji: '🌊' },
    { name: 'Switzerland', emoji: '🏔️' },
    { name: 'India', emoji: '🕌' },
    { name: 'Thailand', emoji: '🏯' },
  ];

  return (
    <div style={{ minHeight: '100vh', background: B.sandPattern }}>
      {/* Header */}
      <div style={{ 
        background: B.night, 
        padding: '60px clamp(16px, 4vw, 40px) 80px', 
        position: 'relative', 
        overflow: 'hidden',
        textAlign: 'center'
      }}>
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg,${B.forest},${B.mint},${B.amber},${B.forest})`, backgroundSize: '200% 100%', animation: 'shimmer 4s linear infinite' }} />
        
        <div style={{ position: 'absolute', inset: 0, opacity: 0.05, backgroundImage: 'radial-gradient(circle,white 1px,transparent 1px)', backgroundSize: '28px 28px' }} />
        
        <p style={{ color: B.mint, fontSize: 11, letterSpacing: 4, textTransform: 'uppercase', fontWeight: 700, fontFamily: "'Montserrat',sans-serif", marginBottom: 16 }}>
          Let's Plan Your Adventure
        </p>
        
        <h1 style={{ 
          fontFamily: "'Playfair Display',serif", 
          fontSize: 'clamp(32px, 5vw, 48px)', 
          color: 'white', 
          fontWeight: 700, 
          marginBottom: 16,
          lineHeight: 1.2
        }}>
          Where do you want to go?
        </h1>
        
        <p style={{ color: 'rgba(255,255,255,0.6)', fontFamily: "'Montserrat',sans-serif", fontSize: 16, maxWidth: 500, margin: '0 auto' }}>
          Enter your destination and choose how you want to plan your trip
        </p>
      </div>

      {/* Main Content */}
      <div style={{ padding: '40px 20px', maxWidth: 600, margin: '-40px auto 0', position: 'relative', zIndex: 1 }}>
        
        {/* Location Input */}
        <div style={{ 
          background: 'white', 
          borderRadius: 24, 
          padding: 24, 
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
          marginBottom: 24
        }}>
          <label style={{ 
            display: 'block', 
            marginBottom: 12, 
            color: B.night, 
            fontSize: 14, 
            fontWeight: 700, 
            fontFamily: "'Montserrat',sans-serif",
            textTransform: 'uppercase',
            letterSpacing: 1
          }}>
            📍 Destination
          </label>
          
          <div style={{ position: 'relative', marginBottom: 20 }}>
            <span style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', fontSize: 20 }}>🌍</span>
            <Input 
              type="text" 
              placeholder="Enter destination (e.g., Paris, Bali, Tokyo)"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleContinue(); }}
              style={{ 
                width: '100%', 
                padding: '16px 16px 16px 50px', 
                borderRadius: 16, 
                border: '2px solid #e0dbd2',
                fontSize: 16,
                fontFamily: "'Montserrat',sans-serif",
                outline: 'none',
                boxSizing: 'border-box',
                transition: 'border-color 0.2s'
              }}
              onFocus={(e) => e.target.style.borderColor = B.forest}
              onBlur={(e) => e.target.style.borderColor = '#e0dbd2'}
            />
          </div>

          {/* Popular Destinations */}
          <div>
            <p style={{ 
              fontSize: 12, 
              color: B.muted, 
              fontFamily: "'Montserrat',sans-serif", 
              marginBottom: 12,
              fontWeight: 600
            }}>
              Popular destinations:
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {popularDestinations.map((dest) => (
                <button
                  key={dest.name}
                  onClick={() => setLocation(dest.name)}
                  style={{
                    background: location === dest.name ? B.forest : 'rgba(0,0,0,0.05)',
                    color: location === dest.name ? 'white' : B.night,
                    border: 'none',
                    padding: '8px 14px',
                    borderRadius: 20,
                    fontSize: 13,
                    fontFamily: "'Montserrat',sans-serif",
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    transition: 'all 0.2s'
                  }}
                >
                  <span>{dest.emoji}</span>
                  <span>{dest.name}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Trip Type Selection */}
        {location && (
          <div style={{ 
            background: 'white', 
            borderRadius: 24, 
            padding: 24, 
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
            animation: 'fadeUp 0.3s ease'
          }}>
            <p style={{ 
              fontSize: 14, 
              color: B.night, 
              fontWeight: 700, 
              fontFamily: "'Montserrat',sans-serif",
              textTransform: 'uppercase',
              letterSpacing: 1,
              marginBottom: 16
            }}>
              How would you like to plan?
            </p>

            {/* Option 1: Plan New Trip */}
            <div 
              role="button" tabIndex={0}
              onClick={() => setTripType('plan')}
              onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setTripType('plan'); } }}
              style={{ 
                background: tripType === 'plan' ? 'rgba(26,107,74,0.08)' : 'rgba(0,0,0,0.02)',
                border: `2px solid ${tripType === 'plan' ? B.forest : 'transparent'}`,
                borderRadius: 20,
                padding: 20,
                marginBottom: 12,
                cursor: 'pointer',
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                gap: 16
              }}
            >
              <div style={{ 
                width: 56, 
                height: 56, 
                borderRadius: 16, 
                background: `linear-gradient(135deg,${B.forest},${B.mint})`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 28,
                flexShrink: 0
              }}>
                ✈️
              </div>
              <div style={{ flex: 1 }}>
                <h3 style={{ 
                  fontFamily: "'Playfair Display',serif", 
                  fontSize: 20, 
                  fontWeight: 700, 
                  color: B.night, 
                  marginBottom: 4 
                }}>
                  Plan a New Trip
                </h3>
                <p style={{ 
                  fontSize: 13, 
                  color: B.muted, 
                  fontFamily: "'Montserrat',sans-serif",
                  lineHeight: 1.5
                }}>
                  AI-powered trip planning with budget calculator, day-by-day itinerary, and local recommendations
                </p>
              </div>
              <div style={{ 
                width: 24, 
                height: 24, 
                borderRadius: '50%', 
                border: `2px solid ${tripType === 'plan' ? B.forest : '#ddd'}`,
                background: tripType === 'plan' ? B.forest : 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                {tripType === 'plan' && <span style={{ color: 'white', fontSize: 14 }}>✓</span>}
              </div>
            </div>

            {/* Option 2: Already Booked */}
            <div 
              role="button" tabIndex={0}
              onClick={() => setTripType('booked')}
              onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setTripType('booked'); } }}
              style={{ 
                background: tripType === 'booked' ? 'rgba(245,166,35,0.08)' : 'rgba(0,0,0,0.02)',
                border: `2px solid ${tripType === 'booked' ? B.amber : 'transparent'}`,
                borderRadius: 20,
                padding: 20,
                cursor: 'pointer',
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                gap: 16
              }}
            >
              <div style={{ 
                width: 56, 
                height: 56, 
                borderRadius: 16, 
                background: `linear-gradient(135deg,${B.amber},#ff8c00)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 28,
                flexShrink: 0
              }}>
                📋
              </div>
              <div style={{ flex: 1 }}>
                <h3 style={{ 
                  fontFamily: "'Playfair Display',serif", 
                  fontSize: 20, 
                  fontWeight: 700, 
                  color: B.night, 
                  marginBottom: 4 
                }}>
                  I've Already Booked
                </h3>
                <p style={{ 
                  fontSize: 13, 
                  color: B.muted, 
                  fontFamily: "'Montserrat',sans-serif",
                  lineHeight: 1.5
                }}>
                  Share your experiences, photos, and connect with others visiting the same place
                </p>
              </div>
              <div style={{ 
                width: 24, 
                height: 24, 
                borderRadius: '50%', 
                border: `2px solid ${tripType === 'booked' ? B.amber : '#ddd'}`,
                background: tripType === 'booked' ? B.amber : 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                {tripType === 'booked' && <span style={{ color: 'white', fontSize: 14 }}>✓</span>}
              </div>
            </div>
          </div>
        )}

        {/* Continue Button */}
        {tripType && (
          <div style={{ marginTop: 24, animation: 'fadeUp 0.3s ease' }}>
            <button 
              onClick={handleContinue}
              style={{ 
                width: '100%', 
                background: `linear-gradient(135deg,${B.forest},${B.mint})`,
                color: 'white',
                border: 'none',
                padding: '18px 32px',
                borderRadius: 50,
                fontSize: 16,
                fontFamily: "'Montserrat',sans-serif",
                fontWeight: 700,
                cursor: 'pointer',
                boxShadow: `0 8px 24px rgba(26,107,74,0.3)`,
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 10
              }}
            >
              Continue to {tripType === 'plan' ? 'Trip Planner' : 'Experience Sharing'}
              <span style={{ fontSize: 20 }}>→</span>
            </button>
          </div>
        )}

        {/* Back Button */}
        <div style={{ textAlign: 'center', marginTop: 20 }}>
          <button 
            onClick={() => goScreen('home')}
            style={{ 
              background: 'none',
              border: 'none',
              color: B.muted,
              fontSize: 14,
              fontFamily: "'Montserrat',sans-serif",
              cursor: 'pointer',
              padding: '10px 20px'
            }}
          >
            ← Back to Home
          </button>
        </div>
      </div>
    </div>
  );
}
